import { Component, OnInit, OnDestroy, ViewEncapsulation, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { OrderService } from '../service/order.service';
import { Order } from '../Module/Order.model';
import { OrderLine } from '../Module/OrderLine.model';
import { DatePipe } from '@angular/common';
import { StateService } from '../service/state.service';
import { LoaderService } from '../service/loader.service';
import { finalize } from 'rxjs/operators';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CellHookData } from 'jspdf-autotable';
import * as QRCode from 'qrcode';
import { AvoirService, Avoir } from '../service/avoir.service';
import { OrderContextService } from '../service/order-context.service';

@Component({
  selector: 'app-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class OrderDetailsComponent implements OnInit, OnDestroy {
  private longPressTimeout: any;
  private tooltipElement: HTMLElement | null = null;
  
  avoirs: Avoir[] = [];
  expandedAvoirs: Set<number> = new Set<number>();

  toggleAvoir(id: number | undefined): void {
    if (id === undefined) return;
    if (this.expandedAvoirs.has(id)) {
      this.expandedAvoirs.delete(id);
    } else {
      this.expandedAvoirs.add(id);
    }
  }

  isAvoirExpanded(id: number | undefined): boolean {
    if (id === undefined) return false;
    return this.expandedAvoirs.has(id);
  }

  constructor(

    private renderer: Renderer2,
    private orderService: OrderService,
    private avoirService: AvoirService,
    private datePipe: DatePipe,
    private stateService: StateService,
    private router: Router,
    private loaderService: LoaderService,
    private orderContextService: OrderContextService
  ) { }

  orderDetails?: Order;
  isLoading: boolean = false;

  ngOnDestroy(): void {
    this.renderer.removeClass(document.body, 'your-component-bg');
    this.endLongPress();
    // Ne pas effacer le contexte ici — il doit persister si l'utilisateur navigue et presse F8 juste après
  }

  ngOnInit(): void {
    this.renderer.addClass(document.body, 'your-component-bg');
    const urlParams = new URLSearchParams(window.location.search);
    const orderId = urlParams.get('id');
    console.log('Retrieved Order ID from URL:', orderId);
    if (orderId) {
      this.getOrderByID(Number(orderId));
    } else {
      console.error('Order ID is null');
    }
  }

  getOrderByID(id: number): void {
    this.isLoading = true;
    this.loaderService.show();

    this.orderService.getOrderById(id)
      .subscribe({
        next: (order: any) => {
          // Fetch avoirs to map returned quantities manually (since backend might be outdated)
          this.avoirService.getAvoirsByOrderId(id)
            .pipe(
              finalize(() => {
                this.isLoading = false;
                this.loaderService.hide();
              })
            )
            .subscribe({
            next: (avoirs) => {
              this.avoirs = avoirs.filter(a => a.status === 'APPROVED' || a.status === 'COMPLETED');
              
              const returnedQtyMap = new Map<number, number>();
              this.avoirs.forEach(a => {
                a.avoirLines.forEach(al => {
                  const pid = al.product?.id;
                  if (pid) {
                    returnedQtyMap.set(pid, (returnedQtyMap.get(pid) || 0) + al.quantityReturned);
                  }
                });
              });

              const mappedLines = order.orderLines?.map((line: any) => {
                const manualReturned = returnedQtyMap.get(line.product?.id) || 0;
                const dbReturned = line.returnedQuantity || 0;
                return new OrderLine(line.product, line.quantity, line.realPurchasePrice, line.realSellingPrice, Math.max(manualReturned, dbReturned));
              });
              
              this.orderDetails = new Order(
                mappedLines,
                order.orderDate,
                order.discount,
                order.isPaid,
                order.clientDTO,
                order.advancePayment,
                order.id,
                order.notes,
                order.isVirtual,
                order.netTotal,
                order.hasReturns,
                order.totalRefunded,
                order.totalDiscountAdjustment,
                order.totalAbsorbedFromDebt
              );
              this.orderContextService.setCurrentOrder(this.orderDetails);
            },
            error: (err) => {
              console.error('Error fetching avoirs', err);
              // Fallback
              const mappedLines = order.orderLines?.map((line: any) => 
                new OrderLine(line.product, line.quantity, line.realPurchasePrice, line.realSellingPrice, line.returnedQuantity)
              );
              this.orderDetails = new Order(mappedLines, order.orderDate, order.discount, order.isPaid, order.clientDTO, order.advancePayment, order.id, order.notes, order.isVirtual, order.netTotal, order.hasReturns, order.totalRefunded, order.totalDiscountAdjustment, order.totalAbsorbedFromDebt);
              this.orderContextService.setCurrentOrder(this.orderDetails);
            }
          });
        },
        error: (error) => {
          console.error('Error fetching order:', error);
          this.isLoading = false;
          this.loaderService.hide();
        }
      });
  }

  calculateDisplayedAvoirsTotal(): number {
    if (!this.avoirs) return 0;
    return this.avoirs.reduce((sum, a) => {
        // Return the Net Value (Total - Adjustment)
        const netValue = (a.totalRefund || 0) - (a.discountAdjustment || 0);
        return sum + netValue;
    }, 0);
  }

  calculateGrossAvoirsTotal(): number {
    if (!this.avoirs) return 0;
    return this.avoirs.reduce((sum, a) => sum + (a.totalRefund || 0), 0);
  }

  getInitialGrossTotal(): number {
    if (!this.orderDetails?.orderLines) return 0;
    return this.orderDetails.orderLines.reduce((sum, line) => {
        return sum + ((line.quantity || 0) * (line.realSellingPrice || 0));
    }, 0);
  }

  getInitialDiscount(): number {
    const currentDiscount = this.orderDetails?.discount || 0;
    const totalAdjustments = this.orderDetails?.totalDiscountAdjustment || 0;
    return currentDiscount + totalAdjustments;
  }

  getNetAvoirTotal(): number {
    if (!this.avoirs) return 0;
    return this.avoirs.reduce((sum, a) => {
        const gross = a.totalRefund || 0;
        const adj = a.discountAdjustment || 0;
        return sum + (gross - adj);
    }, 0);
  }

  setOrderDate(order: Order): string {
    return this.datePipe.transform(order.orderDate, 'dd MMM, yyyy') ?? '';
  }

  goToOrderEdit(order: Order | undefined): void {
    if (order) {
      const url = `/updateOrder?id=${order.id}`;
      window.open(url, '_self');
    }
  }

  // Delete Confirmation Logic
  showDeleteConfirmation: boolean = false;

  deleteOrder(): void {
    if (!this.orderDetails) return;
    this.showDeleteConfirmation = true;
  }

  confirmDelete(): void {
    if (this.orderDetails && this.orderDetails.id) {
      this.isLoading = true;
      this.loaderService.show();
      this.orderService.deleteOrder(this.orderDetails.id!)
        .pipe(finalize(() => {
          this.isLoading = false;
          this.loaderService.hide();
          this.showDeleteConfirmation = false;
        }))
        .subscribe({
          next: () => {
            this.router.navigate(['/orderList']);
          },
          error: (err) => {
            console.error('Error deleting order', err);
          }
        });
    }
  }

  cancelDelete(): void {
    this.showDeleteConfirmation = false;
  }

  private addPageBorder(doc: jsPDF): void {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    doc.setDrawColor(33, 150, 243);
    doc.setLineWidth(0.5);
    doc.rect(margin, margin, pageWidth - 2 * margin, pageHeight - 2 * margin);
  }

  async printInvoice(): Promise<void> {
    if (!this.orderDetails) return;

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;

    const setStyle = (size: number, style: 'normal' | 'bold' | 'italic' = 'normal', color: number[] = [0, 0, 0]) => {
      doc.setFontSize(size);
      doc.setFont('helvetica', style);
      doc.setTextColor(color[0], color[1], color[2]);
    };

    // ─── 1. CORPORATE HEADER ───
    let currentY = 25;
    
    // Left: Company Info (Bold and clear)
    setStyle(16, 'bold', [26, 35, 126]); // Deep Corporate Blue
    doc.text('SOCIÉTÉ HFI', margin, currentY);
    
    setStyle(9, 'normal', [80, 80, 80]);
    doc.text('Avenue Principale, Quartier Industriel\nCasablanca, Maroc\nTel: +212 522 XX XX XX | www.hfi-system.ma', margin, currentY + 6);

    // Right: Document title and main meta
    const docType = this.orderDetails.isVirtual ? 'BON DE COMMANDE' : 'FACTURE';
    setStyle(22, 'normal', [0, 0, 0]);
    doc.text(docType, pageWidth - margin, currentY, { align: 'right' });
    
    // Sub-meta
    setStyle(10, 'normal', [100, 100, 100]);
    doc.text(`Référence : ${this.orderDetails.id}`, pageWidth - margin, currentY + 8, { align: 'right' });
    doc.text(`Date : ${this.setOrderDate(this.orderDetails)}`, pageWidth - margin, currentY + 13, { align: 'right' });

    const isPaid = this.orderDetails.isOrderPaid();
    setStyle(10, 'bold', isPaid ? [46, 125, 50] : [198, 40, 40]);
    doc.text(isPaid ? 'RÉGLÉ' : 'À PAYER', pageWidth - margin, currentY + 18, { align: 'right' });

    // Subtle Separator
    currentY += 25;
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.2);
    doc.line(margin, currentY, pageWidth - margin, currentY);

    // ─── 2. ADDRESS BLOCK ───
    currentY += 10;
    
    setStyle(9, 'bold', [150, 150, 150]);
    doc.text('ADRESSÉ À :', margin, currentY);
    
    setStyle(11, 'bold', [0, 0, 0]);
    const clientName = this.orderDetails.clientDTO ?
      `${this.orderDetails.clientDTO.firstName} ${this.orderDetails.clientDTO.lastName}` : 'CLIENT PASSAGE / DIVERS';
    doc.text(clientName.toUpperCase(), margin, currentY + 6);
    
    setStyle(9, 'normal', [80, 80, 80]);
    const clientPhone = this.orderDetails.clientDTO?.phoneNumber || 'Contact Non Renseigné';
    doc.text(`Tel: ${clientPhone}`, margin, currentY + 11);

    // ─── 3. CORPORATE TABLE (Dark Header) ───
    const tableStartY = currentY + 20;
    
    const tableHead = [['Réf / Description', 'Quantité', 'Prix Unitaire', 'Total Net']];
    
    const tableBody: any[] = this.orderDetails.orderLines?.map(line => {
      const netQty = line.quantity - (line.returnedQuantity || 0);
      const sellingPrice = line.realSellingPrice ?? 0;
      const netTotal = netQty * sellingPrice;
      return [
        line.product?.name || 'Produit Inconnu',
        `${netQty}`,
        `${sellingPrice.toFixed(2)}`,
        `${netTotal.toFixed(2)}`
      ];
    }) || [];

    // Table Returns
    if (this.avoirs && this.avoirs.length > 0) {
      this.avoirs.forEach(avoir => {
        const dateStr = this.datePipe.transform(avoir.approvalDate, 'dd/MM/yyyy');
        tableBody.push([
            { content: `Document d'Avoir N° ${avoir.id} - ${dateStr}`, colSpan: 4, styles: { fillColor: [245, 245, 245], fontStyle: 'bold', textColor: [0,0,0] } }
        ]);
        avoir.avoirLines.forEach(line => {
            const refundPrice = line.refundSellingPrice ?? 0;
            const refundTotal = line.quantityReturned * refundPrice;
            tableBody.push([
                `   Retour : ${line.product?.name}`,
                `-${line.quantityReturned}`,
                `${refundPrice.toFixed(2)}`,
                `-${refundTotal.toFixed(2)}`
            ]);
        });
      });
    }

    autoTable(doc, {
      head: tableHead,
      body: tableBody,
      startY: tableStartY,
      theme: 'plain',
      styles: { font: 'helvetica', fontSize: 9.5, cellPadding: 5, textColor: [50, 50, 50] },
      headStyles: { 
        fillColor: [26, 35, 126], // Corporate Dark Blue (Indigo 900)
        textColor: [255, 255, 255], 
        fontStyle: 'bold'
      },
      bodyStyles: { 
        lineWidth: { bottom: 0.1 }, 
        lineColor: [230, 230, 230] 
      },
      columnStyles: { 
        0: { halign: 'left' },
        1: { halign: 'center', cellWidth: 25 },
        2: { halign: 'right', cellWidth: 35 },
        3: { halign: 'right', cellWidth: 35, fontStyle: 'bold', textColor: [0,0,0] }
      },
      margin: { left: margin, right: margin }
    });

    // ─── 4. SUMMARY BLOCK ───
    currentY = (doc as any).lastAutoTable.finalY + 15;
    if (currentY > pageHeight - 65) { doc.addPage(); currentY = 20; }

    const summaryX = pageWidth - margin - 75;
    const finalTotal = this.getInitialGrossTotal() - (this.orderDetails.discount || 0) - this.calculateGrossAvoirsTotal();

    const drawSummaryRow = (label: string, value: string, isTotal: boolean = false) => {
        if (isTotal) {
            currentY += 2;
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.4);
            doc.line(summaryX, currentY - 5, pageWidth - margin, currentY - 5);
            setStyle(11, 'bold', [0, 0, 0]);
        } else {
            setStyle(9, 'normal', [50, 50, 50]);
        }
        
        doc.text(label, summaryX, currentY);
        doc.text(value, pageWidth - margin, currentY, { align: 'right' });
        currentY += 7;
    };

    drawSummaryRow('Total des lignes', `${this.getInitialGrossTotal().toFixed(2)} MAD`);
    drawSummaryRow('Remise globale', `-${(this.orderDetails.discount || 0).toFixed(2)} MAD`);
    
    if (this.orderDetails.hasReturns) {
        drawSummaryRow('Montant des avoirs', `-${this.calculateGrossAvoirsTotal().toFixed(2)} MAD`);
    }
    
    drawSummaryRow('NET À PAYER', `${finalTotal.toFixed(2)} MAD`, true);

    // ─── 5. CORPORATE FOOTER ───
    currentY = pageHeight - 25;
    
    // Auth QR code mapping to ID and Total
    try {
        const qrData = `Ref:${this.orderDetails.id}|Total:${finalTotal.toFixed(2)}`;
        const qrCodeDataUrl = await QRCode.toDataURL(qrData);
        // Smaller, discreet QR code for professional authenticity
        doc.addImage(qrCodeDataUrl, 'PNG', margin, currentY - 5, 15, 15);
    } catch(e) {}

    // Signature Area
    setStyle(9, 'normal', [150, 150, 150]);
    doc.text('Signature autorisée :', pageWidth - margin - 50, currentY - 5);
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.setLineDashPattern([2, 2], 0);
    doc.line(pageWidth - margin - 50, currentY + 10, pageWidth - margin, currentY + 10);
    doc.setLineDashPattern([], 0); // reset

    // Legal Footer
    setStyle(7, 'normal', [150, 150, 150]);
    doc.text(
        'Ce document est émis par le système de gestion administratif. Les conditions générales de vente s\'appliquent.\nSOCIÉTÉ HFI - Capital: 100,000 MAD - RC: 12345 - IF: 67890 - Casablanca',
        pageWidth / 2, 
        pageHeight - 12, 
        { align: 'center' }
    );

    doc.save(`Document_Achat_${this.orderDetails.id}.pdf`);
  }

  startLongPress(event: TouchEvent, productName: string | undefined): void {
    if (!productName) return;
    this.endLongPress();
    this.longPressTimeout = setTimeout(() => {
      this.tooltipElement = this.renderer.createElement('div');
      this.renderer.addClass(this.tooltipElement, 'product-tooltip');
      this.renderer.setProperty(this.tooltipElement, 'textContent', productName);
      const touch = event.touches[0];
      this.renderer.setStyle(this.tooltipElement, 'position', 'fixed');
      this.renderer.setStyle(this.tooltipElement, 'top', `${touch.clientY - 40}px`);
      this.renderer.setStyle(this.tooltipElement, 'left', `${touch.clientX}px`);
      this.renderer.appendChild(document.body, this.tooltipElement);
    }, 500);
  }

  endLongPress(): void {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
      this.longPressTimeout = null;
    }
    if (this.tooltipElement) {
      this.renderer.removeChild(document.body, this.tooltipElement);
      this.tooltipElement = null;
    }
  }
}
